import { Box, Button, ButtonGroup, Container, Grid, Typography, useTheme } from "@mui/material"
import { useApi } from "auth/useApi3"
import { AppContext } from "components/AppContextProvider"
import ClpTable from "components/ClpTable"
import { ClpTableEnum } from "components/ClpTable/ClpTableEnum"
import { GlobalLoaderContext } from "components/GlobalLoadingProvider"
import { apiEndpoints } from "generated/apiEndpoints"
import { useContext, useEffect, useMemo, useState } from "react"
import { StyledHeaderContainer, StyledTableContainer } from "shared/styled/Global"
import CustomFieldForm from "./CustomFieldForm/CustomFieldForm"
import { t } from "i18next"
import { MultiSelectFilter } from "components/ClpTable/ClpTableFilterTypes"
import { DynamicTooltip } from "components/Tooltip/DynamicTooltip"

const tableDesc = `Custom fields are used to add <strong>extra information or custom data</strong> to vehicles that are not in the system's standard fields. <br />They can be freely created as needed and connected to <strong>individual vehicles</strong> or <strong>entire vehicle groups</strong> to collect the right data in the right place.`

const CustomFields = () => {
  const appContext = useContext(AppContext)
  const loadingContext = useContext(GlobalLoaderContext)
  const [listResult, listApi] = useApi()
  const [addResult, addApi] = useApi()
  const [updateResult, updateApi] = useApi()
  const [editItemResult, editItemApi] = useApi()
  const [editMode, setEditMode] = useState(false)
  const theme = useTheme()

  useEffect(() => {
    loadingContext.setLoading(() => listResult.status === 1)
  })

  useEffect(() => {
    if (updateResult.status === 2 || addResult.status === 2) {
      listApi.get(apiEndpoints.vehicleregistryadmin.listcustomfields)
      appContext.toggleSideBar(false)
    }
  }, [updateResult, addResult, listApi])

  useEffect(() => {
    listApi.get(apiEndpoints.vehicleregistryadmin.listcustomfields)

    return () => {
      listApi.leave()
    }
  }, [listApi])

  const onAddSubmit = (values) => {
    addApi.post(apiEndpoints.vehicleregistryadmin.addcustomfield, values)
  }

  const onUpdateSubmit = (values) => {
    updateApi.put(apiEndpoints.vehicleregistryadmin.updatecustomfield, values)
  }

  const openEdit = (id) => {
    appContext.toggleSideBar(true)
    setEditMode(true)
    editItemApi.get(apiEndpoints.vehicleregistryadmin.getcustomfieldbyid.replace("{id}", id))
    updateApi.reset()
    addApi.reset()
  }

  const openAdd = () => {
    appContext.toggleSideBar(true)
    setEditMode(false)
    editItemApi.reset()
    updateApi.reset()
    addApi.reset()
  }

  const columns = [
    {
      accessorKey: "name",
      header: t("global.name", "Name")
    },
    {
      accessorKey: "type",
      header: t("global.type", "Type"),
      cell: (cell) => {
        switch (cell.row.original.type) {
          case "Numeric":
            return t("global.number", "Number")
          case "Date":
            return t("global.date", "Date")
          case "Boolean":
            return t("global.yes_no", "Yes/No")
          case "Text":
            return t("global.textfield", "Textfield")
          case "RepeatedNotification":
            return t("global.recurring_reminder", "Recurring reminder")
          case "Notification":
            return t("global.reminder", "Reminder")
          default:
            return ""
        }
      }
    },
    {
      accessorFn: (row) => (row.isRequired ? "true" : "false"),
      header: t("global.mandatory", "Mandatory"),
      filterFn: "arrIncludesSome",
      meta: {
        defaultValue: [],
        filterHeader: t("global.mandatory", "Mandatory"),
        filter: MultiSelectFilter,
        filterOptions: [
          { value: "true", name: t("global.yes", "Yes") },
          { value: "false", name: t("global.no", "No") }
        ]
      },
      cell: (cell) => (cell.row.original.isRequired ? t("global.yes", "Yes") : t("global.no", "No"))
    },
    {
      accessorFn: (row) => (row.active ? "true" : "false"),
      header: t("global.active", "Active"),
      filterFn: "arrIncludesSome",
      meta: {
        defaultValue: [],
        filterHeader: t("global.active", "Active"),
        filter: MultiSelectFilter,
        filterOptions: [
          { value: "true", name: t("global.yes", "Yes") },
          { value: "false", name: t("global.no", "No") }
        ]
      },
      cell: (cell) => (cell.row.original.active ? t("global.yes", "Yes") : t("global.no", "No"))
    }
  ]

  return (
    <>
      <StyledHeaderContainer>
        <Box>
          <Typography variant="h1">{t("global.custom_fields", "Custom Fields")}</Typography>
        </Box>
        <ButtonGroup color="primary">
          <Button
            color="primary"
            onClick={() => {
              openAdd()
            }}
            variant="contained">
            {t("global.add_field", "Add field")}
          </Button>
        </ButtonGroup>
      </StyledHeaderContainer>

      <StyledTableContainer>
        <Box px={{ xs: 2, sm: 3 }} pt={3}>
          <CustomFieldForm
            showSidebar={appContext.isSidebarOpen}
            setShowSidebarForm={appContext.toggleSideBar}
            editItemResult={editItemResult}
            addResult={addResult}
            updateResult={updateResult}
            editMode={editMode}
            onAddSubmit={onAddSubmit}
            onUpdateSubmit={onUpdateSubmit}
          />
          <Grid container direction="row" justifyContent="space-between" gap={3}>
            <Box display="flex" alignItems="center" gap={1}>
              <Typography variant="labelSmall">{t("global.information", "Information")}</Typography>
              <DynamicTooltip t={t} title={t("global.custom_fields", "Custom Fields")}>
                <Grid item>
                  <Typography fontWeight="600" variant="labelSmall">
                    {t("vehicleregistry.how_to_use_custom_fields", "How to use custom fields")}
                  </Typography>
                  <Typography
                    sx={{ mt: 0.5 }}
                    variant="bodySmall"
                    color="textSecondary"
                    dangerouslySetInnerHTML={{
                      __html: t("custom_fields.table_desc", tableDesc)
                    }}
                  />
                </Grid>
              </DynamicTooltip>
            </Box>
          </Grid>
        </Box>
        <ClpTable
          columns={columns}
          onRowClick={(row) => openEdit(row.original.id)}
          rows={listResult.status === 2 && listResult.data}
          status={listResult.status}
          loadingState={listResult.status}
          tableId={ClpTableEnum.VehicleRegistrySettingsCustomFields}
        />
      </StyledTableContainer>
    </>
  )
}

export default CustomFields
